import { Wifi, WifiOff, Cloud, Loader2, Check } from 'lucide-react';
import { ConnectionStatus, CollaborationUser } from '../lib/collaboration';
import { cn } from '../lib/utils';

interface ConnectionStatusProps {
  status: ConnectionStatus;
  lastSaved?: number | null;
}

export function ConnectionStatusIndicator({ status, lastSaved }: ConnectionStatusProps) {
  const getStatusConfig = () => {
    switch (status) {
      case 'connecting':
        return {
          icon: Loader2,
          label: 'Connecting...',
          className: 'text-yellow-500',
          animate: true,
        };
      case 'connected':
        return {
          icon: Wifi,
          label: 'Connected',
          className: 'text-green-500',
          animate: false,
        };
      case 'saving':
        return {
          icon: Cloud,
          label: 'Saving...',
          className: 'text-blue-500',
          animate: true,
        };
      case 'saved':
        return {
          icon: Check,
          label: lastSaved ? `Saved ${formatTimeAgo(lastSaved)}` : 'Saved',
          className: 'text-green-500',
          animate: false,
        };
      case 'disconnected':
        return {
          icon: Loader2,
          label: 'Reconnecting…',
          className: 'text-orange-500',
          animate: true,
        };
      case 'offline':
        return {
          icon: WifiOff,
          label: 'Offline (edits local)',
          className: 'text-red-500',
          animate: false,
        };
      default:
        return {
          icon: WifiOff,
          label: 'Unknown',
          className: 'text-gray-500',
          animate: false,
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div className={cn('flex items-center gap-1.5 text-xs', config.className)}>
      <Icon className={cn('h-3.5 w-3.5', config.animate && 'animate-spin')} />
      <span>{config.label}</span>
    </div>
  );
}

interface UserAvatarsProps {
  users: CollaborationUser[];
  maxDisplay?: number;
}

export function UserAvatars({ users, maxDisplay = 3 }: UserAvatarsProps) {
  // Deduplicate users by ID to prevent duplicate avatars
  const uniqueUsers = users.filter((user, index, self) =>
    index === self.findIndex(u => u.id === user.id)
  );
  
  const displayUsers = uniqueUsers.slice(0, maxDisplay);
  const remainingCount = uniqueUsers.length - maxDisplay;

  if (uniqueUsers.length === 0) return null;

  return (
    <div className="flex items-center -space-x-2">
      {displayUsers.map((user) => (
        <div
          key={user.id}
          className="relative h-7 w-7 rounded-full border-2 border-background flex items-center justify-center text-xs font-medium text-white"
          style={{ backgroundColor: user.color }}
          title={user.name}
        >
          {user.name.charAt(0).toUpperCase()}
        </div>
      ))}
      {remainingCount > 0 && (
        <div className="relative h-7 w-7 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-medium">
          +{remainingCount}
        </div>
      )}
    </div>
  );
}

interface CollaborationBarProps {
  status: ConnectionStatus;
  users: CollaborationUser[];
  lastSaved?: number | null;
  onReconnect?: () => void;
}

export function CollaborationBar({ status, users, lastSaved, onReconnect }: CollaborationBarProps) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2 border-b bg-card/50 backdrop-blur-sm">
      <UserAvatars users={users} />
      <div className="flex items-center gap-3">
        <ConnectionStatusIndicator status={status} lastSaved={lastSaved} />
        {(status === 'offline' || status === 'disconnected') && onReconnect && (
          <button
            onClick={onReconnect}
            className="btn-ghost px-2 py-1 text-xs h-7"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}
